import { FC, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, Route, Switch, useLocation } from 'react-router';
import { reloadHeaderFinished, setStickyMode } from '../../store/header/actions';
import { fetchSelectionlists } from '../../store/orderlist/actions';
import { findUsers, impersonateUser, setAuthenticatedSpocById } from '../../store/authentication/actions';
import SelectionListBar from '../navigation/SelectionListBar';
import SearchBar from '../search/SearchBar';
import { PageHeader } from './PageHeader';
import { ScrollHeader } from './ScrollHeader';
import { SearchScope } from '../../store/search/state';
import { AppState } from '../../store/appState';
import { Orderlist } from '../../../models/OrderList';
import { useLibraryManagedProductGroupsFeaturePages } from '../../yourborrowbox/marketing/productgroups/api/useLibraryManagedProductGroupsFeaturePages';
import {
    getCatalogueSearchScopes,
    getYourBorrowBoxSearchScopes,
    SCOPE_ALL_OBJECT,
    SCOPE_CATALOGUE_OBJECT
} from '../../store/search/actions';
import { deriveSearchScopeFromPathname } from '../search/utils/deriveSearchScopeFromPathname';
import toLower from 'lodash/toLower';
import { usePreviousProps } from '../../hooks/usePreviousProps';
import { OrderListsState } from '../../store/orderlist/state';
import historyUtils from '../utils/utils-routing';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { invalidateCartSummaryQuery, useCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import { useAddToList } from '../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../api/shopping-cart/useAddToCart';
import { COLLECTION_NAVIGATION_V2, MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { invalidateShoppingCartProducts } from '../../api/shopping-cart/useShoppingCartProducts';
import { invalidateCartGroupedItems } from '../../api/shopping-cart/useCartGroupedItems';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { invalidateSelectionListSummary } from '../../api/selection-list/useSelectionListSummary';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { invalidateSelectionListGroupedItems } from '../../api/selection-list/useSelectionListGroupedItems';

const SHOP_PATH_NAMES = [
    '/shop/:shopLanguage?/:loanFormat?',
    '/catalogue/:loanFormat?', // TODO [multi-language-shop] legacy
    '/search',
    '/unlimited', // TODO [multi-language-shop] legacy
    '/cart',
    '/checkout',
    '/assistant',
    '/bundles',
    '/orderlists',
    '/language', // TODO [multi-language-shop] legacy
    '/libxchange', // TODO [multi-language-shop] legacy
    '/products'
];

const COLLECTION_PATH_NAMES = ['/collection', '/localcontent', '/marketing', '/collectionmgmt', '/merchandising'];

export const HeaderContainer: FC = () => {
    const isCollectionNavigationV2Enabled = useFeature(COLLECTION_NAVIGATION_V2);
    const location = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { displayFilter: displayFilterQueryParam } = queryString.parse(location.search);
    const displayFilter = displayFilterQueryParam === null;

    const authentication = useSelector((state: AppState) => state.authentication);

    const orderlists = useSelector((state: AppState) => state.orderlists);
    const previousOrderLists = usePreviousProps<OrderListsState>(orderlists);
    const orderlist = useSelector((state: AppState) => state.orderlist);
    const productToSelectionListAnimation = useSelector(
        (state: AppState) => state.orderlists.productToSelectionListAnimation
    );
    const headerState = useSelector((state: AppState) => state.header);

    const headerContainerRef = useRef<HTMLDivElement>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [openSearchBar, setOpenSearchBar] = useState(false);
    const [isSticky, setIsSticky] = useState(false);

    const [shopSearchScope, setShopSearchScope] = useState<SearchScope>({} as SearchScope);
    const [collectionSearchScope, setCollectionSearchScope] = useState<SearchScope>({} as SearchScope);

    const searchScope = deriveSearchScopeFromPathname(location.pathname);
    const toggleStickyAt = 106;
    const toggleStickyWithThirdLine = 185;
    const isInYourBorrowBox = COLLECTION_PATH_NAMES.some(pathName => location.pathname.includes(pathName));
    const hasThirdLine = !!matchPath(location.pathname, COLLECTION_PATH_NAMES);

    const hasChangedOrdlerList =
        (previousOrderLists?.active && orderlists?.active && previousOrderLists?.active.id !== orderlists.active.id) ??
        false;

    useEffect(() => {
        if (isInYourBorrowBox) {
            setCollectionSearchScope(searchScope ?? SCOPE_ALL_OBJECT);
        }
        setShopSearchScope(searchScope ?? SCOPE_CATALOGUE_OBJECT);
        return () => {
            setCollectionSearchScope({} as SearchScope);
            setShopSearchScope({} as SearchScope);
        };
    }, [searchScope, isInYourBorrowBox]);

    const handleScroll = useCallback(() => {
        const toggleStickyValue = hasThirdLine ? toggleStickyWithThirdLine : toggleStickyAt;

        if ((window.scrollY || window.pageYOffset) > toggleStickyValue) {
            if (!isSticky) {
                setIsSticky(true);
                dispatch(setStickyMode(true));
            }
        } else {
            if (isSticky) {
                setIsSticky(false);
                dispatch(setStickyMode(false));
            }
        }
    }, [dispatch, hasThirdLine, isSticky]);

    useEffect(() => {
        // We have to re-fetch the selection lists endpoint when the library scope is changed
        // otherwise we don't see the selection list dropdown with the current Library scope
        if (orderlists.list.length === 0 || authentication.spoc.id) {
            dispatch(fetchSelectionlists());
        }
    }, [authentication.spoc.id, dispatch, orderlists.list.length]);

    useEffect(() => {
        dispatch(reloadHeaderFinished());
    }, [dispatch, orderlists.list.length]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [dispatch, handleScroll]);

    function onOpenSearchBar() {
        setOpenSearchBar(true);
    }

    function onCloseSearchBar() {
        setOpenSearchBar(false);
    }

    function onSearchQueryChange(newQuery: string) {
        setSearchQuery(newQuery);
    }

    const { addToList } = useAddToList({
        callback: targetListId => {
            invalidateSelectionListAggregations(targetListId).then();
            invalidateSelectionListSummary(targetListId).then();
            invalidateSelectionListProducts(targetListId).then();
            invalidateSelectionListGroupedItems(targetListId).then();
        }
    });
    const { addToCart } = useAddToCart({
        callback: () => {
            invalidateCartAggregations().then();
            invalidateShoppingCartProducts().then();
            invalidateCartGroupedItems().then();
            invalidateCartSummaryQuery().then();
        }
    });

    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);

    const activeList = (orderlist && orderlist.id ? orderlist : orderlists.list[0] || {}) as Orderlist;
    const lastActiveList = orderlists.active || orderlists.list[0] || {};
    const headerClasses = classNames('header', {
        'with-sl-bar-exp': orderlists.selectionListBarExpanded
    });

    const searchBar = (
        <Switch>
            <Route path={SHOP_PATH_NAMES}>
                <SearchBar
                    searchBarActive={openSearchBar}
                    searchQuery={searchQuery}
                    onSearchQueryChange={onSearchQueryChange}
                    onCloseSearchBar={onCloseSearchBar}
                    onOpenSearchBar={onOpenSearchBar}
                    orderlist={orderlist}
                    orderlists={orderlists.list}
                    onAddToCart={addToCart}
                    onAddToList={addToList}
                    isSticky={isSticky}
                    searchScope={shopSearchScope}
                    changeSearchScope={setShopSearchScope}
                    searchScopes={getCatalogueSearchScopes(isMultiLanguageShopEnabled)}
                    searchLabel={t('components.searchBar.shopSearch')}
                    getSearchPath={() => '/search'}
                    isInYourBorrowBox={isInYourBorrowBox}
                />
            </Route>
            <Route path={COLLECTION_PATH_NAMES}>
                <SearchBar
                    searchBarActive={openSearchBar}
                    searchQuery={searchQuery}
                    onSearchQueryChange={onSearchQueryChange}
                    onCloseSearchBar={onCloseSearchBar}
                    onOpenSearchBar={onOpenSearchBar}
                    orderlist={orderlist}
                    orderlists={orderlists.list}
                    onAddToCart={addToCart}
                    onAddToList={addToList}
                    isSticky={isSticky}
                    searchScope={collectionSearchScope}
                    changeSearchScope={setCollectionSearchScope}
                    searchScopes={getYourBorrowBoxSearchScopes()}
                    searchLabel={t('components.searchBar.collectionSearch')}
                    getSearchPath={scopeKey =>
                        isCollectionNavigationV2Enabled
                            ? `/collectionmgmt/search/${toLower(scopeKey)}`
                            : `/collection/search/${toLower(scopeKey)}`
                    }
                    isInYourBorrowBox={isInYourBorrowBox}
                />
            </Route>
        </Switch>
    );

    useLibraryManagedProductGroupsFeaturePages();
    const cartSummaryQuery = useCartSummaryQuery();

    return (
        <div ref={headerContainerRef} className={headerClasses} data-displayname="HeaderContainer">
            <PageHeader
                authentication={authentication}
                changeSpoc={spoc => dispatch(setAuthenticatedSpocById(spoc.id, location.pathname, '/catalogue'))}
                searchbar={!isSticky && searchBar}
                selectionLists={orderlists.list}
                impersonate={user => dispatch(impersonateUser(user))}
                findUsers={(searchString?: string) => dispatch(findUsers(searchString))}
                lastActiveList={lastActiveList}
                productToSelectionListAnimation={productToSelectionListAnimation}
                productTitle={headerState.productTitle}
                hasListChanged={hasChangedOrdlerList}
                cartSummaryQuery={cartSummaryQuery}
            />
            {isSticky ? (
                <ScrollHeader
                    orderlist={orderlist}
                    show={isSticky}
                    toggleFilterDisplay={() =>
                        historyUtils.replaceURLSearchState('displayFilter', displayFilter ? undefined : null, false)
                    }
                    searchbar={searchBar}
                    onOpenSearchBar={onOpenSearchBar}
                    activeList={activeList}
                    productTitle={headerState.productTitle}
                    spoc={authentication.spoc}
                    cartSummaryQuery={cartSummaryQuery}
                />
            ) : null}
            <SelectionListBar isSticky={isSticky} />
        </div>
    );
};
